// @ts-strict-ignore
import React from 'react'
import { IonLoading, IonToast } from '@ionic/react'
import mapboxgl from 'mapbox-gl'

import { CollectionItem, ScreenSection, AppMetaData, MediaItemsStoreState } from 'mytours-read-common'
import { MapItem } from 'mytours-read-api-typescript'

import { generateMapboxStyleUrl, getStyleProperty, isRTL, getCurrentLanguage } from '../../platform'
import { DEFAULT_APP_INFO, loadAppMetaDataFromDisk } from '../../settings/functions'
import {
	SwitchDirection,
	getBounds,
	switchItemAction,
	getDirectionRoute,
	getCurrentPosition,
	findClosestPointIndex,
	formatMapPinIcon,
	buildPointMapPinPopup,
} from '../functions'

import { MapControls, Marker, SimpleCoordinates, RouteProfile, MapContainer } from '../types'

import strings from '../../../strings'

import 'mapbox-gl/dist/mapbox-gl.css'
import { generateMapPinIconUrl } from 'common_lib/maps/functions'
import { ionHistory } from 'mobile_web/modules/navigation'
import { ElevationGraph } from './ElevationGraph'

interface Props extends MapContainer {
	items: CollectionItem[]
	currentItemIndex: number
	mapControls?: (params: MapControls) => JSX.Element
	extractMapboxMapStyle?: () => string | undefined
	onCloseMap?: () => void
	mapStyle?: string
	boundAll?: boolean
	onSetActiveItem?: (item: CollectionItem | ScreenSection | MapItem, itemIndex: number) => void
	defaultZoom?: number
	mapRoutes?: any[]
	showDirections?: boolean
	routeProfile?: RouteProfile
	endCoordinates?: SimpleCoordinates
	setLegsData?: (legsData: any) => void
	showInAppDirections?: (showInAppDirections: boolean) => void
	onMapReady?: () => void
	isItemsLoaded?: boolean
	onMapResize?(): void
	mediaItems?: MediaItemsStoreState
	loadMediaItem?: (id: number, version: number, language: string) => void
	elevationEnabled?: boolean
}

interface State {
	markers: Marker[]
	markerListeners: EventListenerOrEventListenerObject[]
	mapContainerReady: boolean
	coordinates: SimpleCoordinates
	loading: boolean
	error: string
	map: mapboxgl.Map
	mediaItemsLoaded: boolean
	// Elevation
	currentRoute?: [number, number, number][]
	selectedRoutePointIndex?: number
	elevationOpened: boolean
}

const INITIAL_STATE: State = {
	markers: [],
	markerListeners: [],
	mapContainerReady: false,
	coordinates: undefined,
	loading: true,
	error: '',
	map: null,
	mediaItemsLoaded: false,
	elevationOpened: false,
}

export default class MapboxMap extends React.Component<Props, State> {
	state: State = INITIAL_STATE
	mapContainer: HTMLElement
	geolocate = undefined
	markerClicked = false
	mapZoommed = false // keep zoom when switch stops
	appData: AppMetaData = null
	renderePointMarkersOnce = false
	activeElevationMarker: mapboxgl.Marker

	private unListen: () => void

	constructor(props: Props) {
		super(props)

		const el = document.createElement('div')
		el.className = 'elevation-marker'
		this.activeElevationMarker = new mapboxgl.Marker(el)
	}

	componentDidMount(): void {
		// Use the next listener to resize the map
		// Ex: when a user was on the map, then opened another page and come back to the map. It will be resized using the next history listener
		this.unListen = ionHistory.listen(() => {
			// setTimeout can be 0, because we need it only for async resizing
			setTimeout(() => this.state.map.resize(), 0)
			this.props.onMapResize?.()
		})
	}

	componentDidUpdate = (prevProps: Props) => {
		if (this.state.map) {
			if (this.mapContainer && this.mapContainer.offsetWidth > 0 && this.mapContainer.offsetHeight > 0) {
				setTimeout(() => {
					this.state.map.resize()
					this.props.onMapResize?.()
				}, 0)
			}

			if (prevProps.currentItemIndex !== this.props.currentItemIndex && this.props.currentItemIndex != null) {
				this.setActiveMarkerIcon(this.props.items[this.props.currentItemIndex])

				const item = this.props.items[this.props.currentItemIndex]
				const { defaultZoom } = this.props

				if (item?.lng && item?.lat) {
					const ll = new mapboxgl.LngLat(Number(item.lng), Number(item.lat))
					this.state.map.flyTo({
						center: ll,
						zoom: defaultZoom || (this.mapZoommed ? this.state.map.getZoom() : 15),
					})
				}
			}

			if (this.props.routeProfile && prevProps.routeProfile != this.props.routeProfile) {
				this.getRoute(this.props.routeProfile, this.state.coordinates)
			}

			if (this.state.map.isStyleLoaded?.() && prevProps.mapRoutes !== this.props.mapRoutes && this.props.mapRoutes) {
				this.drawMapRoutes()
				this.fetchMediaItems()
			}

			// Always render even if they don't use media item
			if (
				this.state.mediaItemsLoaded &&
				(this.props.mediaItems !== prevProps.mediaItems ||
					(this.getMediaItemIds().length === 0 && !this.renderePointMarkersOnce))
			) {
				this.renderePointMarkersOnce = true
				this.createPointMarkers()
			}
		}
	}

	componentWillUnmount = () => {
		if (this.state.map) {
			this.state.map?.remove()
		}

		// Unbind all manually set up listeners
		this.state.markerListeners.forEach(listener => document.removeEventListener('click', listener))

		this.unListen()
	}

	setUpMap = async () => {
		const {
			items,
			currentItemIndex,
			mapStyle,
			extractMapboxMapStyle,
			defaultZoom,
			showDirections,
			routeProfile,
			onMapReady,
		} = this.props
		const hasCoordinates =
			items && items[currentItemIndex] && items[currentItemIndex].lat && items[currentItemIndex].lng

		const mapStyleId: string = extractMapboxMapStyle && extractMapboxMapStyle()
		const mapStyleUrl: string | undefined = mapStyleId && generateMapboxStyleUrl(mapStyleId)

		const mapOptions: mapboxgl.MapboxOptions = {
			container: this.mapContainer,
			style: mapStyle || mapStyleUrl,
			zoom: defaultZoom || 8,
		}

		let coordinates

		if (hasCoordinates) {
			mapOptions.center = [Number(items[currentItemIndex].lng), Number(items[currentItemIndex].lat)]
		} else if (showDirections) {
			coordinates = await getCurrentPosition()
			this.setState({ coordinates })
			mapOptions.center = [coordinates.lng, coordinates.lat]
		}

		this.geolocate = new mapboxgl.GeolocateControl({
			positionOptions: {
				enableHighAccuracy: true,
			},
			trackUserLocation: true,
		})

		const map = new mapboxgl.Map(mapOptions)
		map.addControl(new mapboxgl.NavigationControl(), isRTL() ? 'top-right' : 'top-left')
		map.addControl(this.geolocate)

		map.on('load', () => {
			if (showDirections) {
				this.getMyPosition()
				this.getRoute(routeProfile, coordinates)
			} else {
				this.createMarkers()
				this.drawMapRoutes()
				this.fetchMediaItems()
				this.initClickOnMap()
			}

			onMapReady?.()
		})

		this.setState({ map })
	}

	initClickOnMap = (): void => {
		this.state.map.on('click', e => {
			try {
				const targetElement: any = e.originalEvent.target
				if (!targetElement.classList.contains('custom-marker')) {
					this.setActiveMarkerIcon(null)
					if (this.props.onSetActiveItem) {
						this.props.onSetActiveItem(null, null)
					}
				}
			} catch (error) {
				console.log('error', error)
			}
		})
	}

	getMediaItemIds = (): number[] => {
		const { mapRoutes } = this.props
		const ids: number[] = []
		const pointsRoute = mapRoutes.find(r => r?.features?.[0]?.geometry?.type === 'Point')
		if (pointsRoute) {
			pointsRoute.features.forEach(f => {
				if (f.properties.media_item_id) {
					ids.push(f.properties.media_item_id)
				}
			})
		}

		return ids
	}

	fetchMediaItems = (): void => {
		const { mapRoutes, loadMediaItem } = this.props
		const { mediaItemsLoaded } = this.state
		const mediaItemIds = this.getMediaItemIds()
		const pointsRoute = mapRoutes.find(r => r?.features?.[0]?.geometry?.type === 'Point')
		if (pointsRoute && !mediaItemsLoaded && loadMediaItem) {
			mediaItemIds.forEach(id => {
				loadMediaItem(id, 1, getCurrentLanguage())
			})
			this.setState({ mediaItemsLoaded: true })
		}
	}

	getPopupMaxWidth = (): string => {
		if (window.innerWidth >= 409 && window.innerWidth <= 540) {
			return '360px'
		} else if (window.innerWidth > 540) {
			return '460px'
		} else {
			return '300px'
		}
	}

	createPointMarker = async (feat: any): Promise<void> => {
		const {
			properties,
			geometry: { coordinates },
		} = feat
		const { icon, media_item_id } = properties

		const [iconStyle, iconColor, iconIcon]: string[] = icon?.split('_')

		const el = document.createElement('div')
		el.className = 'custom-marker'
		el.style.backgroundImage = this.generateMapPinIconUrl(iconStyle, iconColor.replace('#', ''), iconIcon)

		const mediaItem = this.props.mediaItems?.get(media_item_id)
		const popupHTML = buildPointMapPinPopup(feat, mediaItem?.item)

		const popup = new mapboxgl.Popup({
			offset: 50,
			closeButton: false,
			maxWidth: this.getPopupMaxWidth(),
		}).setHTML(popupHTML.innerHTML)

		new mapboxgl.Marker(el)
			.setLngLat([Number(coordinates[0]), Number(coordinates[1])])
			.setPopup(popup)
			.addTo(this.state.map)
	}

	createPointMarkers = (): void => {
		// It's probably a good idea to remove the old marker if this is called multiple times
		const pointsRoute = this.props.mapRoutes?.find(r => r?.features?.[0]?.geometry?.type === 'Point')
		pointsRoute.features.map(f => this.createPointMarker(f))
	}

	drawMapRoutes = (): void => {
		const { mapRoutes, elevationEnabled } = this.props
		if (mapRoutes && mapRoutes.length > 0) {
			mapRoutes.forEach(route => {
				if (!this.state.map.getSource(`route${route?.attributes?.id}`)) {
					this.state.map.addSource(`route${route?.attributes?.id}`, {
						type: 'geojson',
						data: route,
					})

					if (route?.features?.[0]?.geometry?.type === 'Point') {
						this.state.map.addLayer({
							id: `routepoints${route?.attributes?.id}`,
							type: 'circle',
							source: `route${route?.attributes?.id}`,
							paint: {
								'circle-radius': 6,
								'circle-color': getStyleProperty('--nav-bg'),
							},
						})
					} else {
						this.state.map.addLayer({
							id: `routeline${route?.attributes?.id}`,
							type: 'line',
							source: `route${route?.attributes?.id}`,
							layout: {
								'line-join': 'round',
								'line-cap': 'round',
							},
							paint: {
								'line-color': ['coalesce', ['get', 'line-color'], getStyleProperty('--nav-bg')],
								'line-width': ['interpolate', ['linear'], ['zoom'], 12, 3, 22, 12],
							},
						})

						// Make the clickable layer thicker to make it easier to click
						const clickableLineLayer = `routeline${route?.attributes?.id}-clickable`
						this.state.map.addLayer({
							id: clickableLineLayer,
							type: 'line',
							source: `route${route?.attributes?.id}`,
							layout: {
								'line-join': 'round',
								'line-cap': 'round',
							},
							paint: {
								'line-color': 'transparent',
								'line-width': 20,
							},
						})

						if (elevationEnabled) {
							const coordinates = route?.features?.[0]?.geometry?.coordinates
							this.state.map.on('click', clickableLineLayer, e => {
								this.handleLineClick(coordinates || [], [e.lngLat.lng, e.lngLat.lat])
							})
							this.state.map.on('mousemove', clickableLineLayer, e => {
								if (this.state.elevationOpened && this.state.currentRoute && this.state.currentRoute === coordinates) {
									this.handleLineClick(coordinates || [], [e.lngLat.lng, e.lngLat.lat])
								}
							})
						}
					}
				}
			})
		}
	}

	handleLineClick = (route: number[][], target: [number, number]) => {
		// Check for elevation
		if (route.length === 0 || typeof route[0][2] !== 'number') {
			return
		}

		const currentRoute: [number, number, number][] = route as [number, number, number][]

		const closestPointIndex = findClosestPointIndex(currentRoute, target)
		const closestPoint = currentRoute[closestPointIndex]

		this.activeElevationMarker.setLngLat([closestPoint[0], closestPoint[1]]).addTo(this.state.map)
		this.setState({ currentRoute, selectedRoutePointIndex: closestPointIndex, elevationOpened: true })
	}

	setActiveElevationMarkerCoords = (coords: [number, number]) => {
		this.activeElevationMarker.setLngLat(coords).addTo(this.state.map)
	}

	hideActiveElevationMarker = () => {
		this.activeElevationMarker.remove()
	}

	toggleElevationGraph = () => {
		this.setState({ selectedRoutePointIndex: undefined, elevationOpened: !this.state.elevationOpened })
	}

	createMarkers = async () => {
		const { items, boundAll } = this.props
		const newMarkers = []

		for await (const item of items) {
			const hasCoordinates = item && item.lat && item.lng

			if (item.item_number && !this.appData) {
				this.appData = await loadAppMetaDataFromDisk(DEFAULT_APP_INFO.projectId)
			}

			if (hasCoordinates) {
				const marker = await this.createMarker(item)
				newMarkers.push(marker)
			}
		}

		this.setState({ markers: [...newMarkers] })
		setTimeout(() => this.fitBounds(boundAll), 1000)
	}

	createMarker = (collectionItem: CollectionItem): Promise<Marker> =>
		new Promise(resolve => {
			const { currentItemIndex, items } = this.props

			const { lat, lng, id, item_number: itemNumber } = collectionItem

			let { map_pin_colour: mapPinColour, map_pin_style: mapPinStyle, map_pin_icon: mapPinIcon } = collectionItem

			if ('stock_icon_pin_colour' in collectionItem) {
				mapPinColour = (collectionItem as any).stock_icon_pin_colour
				mapPinIcon = (collectionItem as any).stock_icon_pin_icon
				mapPinStyle = (collectionItem as any).stock_icon_pin_style
			}

			// Create new marker, coordinates and adding it to map
			const isActiveMarker = items[currentItemIndex] && items[currentItemIndex].id == collectionItem.id

			const icon = formatMapPinIcon({
				mapPinIcon,
				itemNumber,
				keypadEnabled: this.appData?.keypad_enabled,
				keypadDigits: this.appData?.keypad_digits,
			})

			const el = document.createElement('div')
			el.className = `custom-marker${isActiveMarker ? ' active' : ''}`
			el.style.backgroundImage = this.generateMapPinIconUrl(mapPinStyle, mapPinColour, isActiveMarker ? 'active' : icon)

			const marker = new mapboxgl.Marker(el).setLngLat([Number(lng), Number(lat)]).addTo(this.state.map)

			// This is a really fugly way of doing things, but Mapbox do not provide an onClick event
			// for markers
			if ((marker as any)._element) {
				;(marker as any)._element.addEventListener('click', this.bindMarkerOnClick(collectionItem))
			}

			resolve({
				marker,
				lat,
				lng,
				collectionItemId: id,
				pinColor: mapPinColour,
				pinIcon: icon,
				pinStyle: mapPinStyle,
			})
		})

	bindMarkerOnClick = (collectionItem: CollectionItem | ScreenSection) => {
		const listener = () => {
			this.markerClicked = true
			const { items, onSetActiveItem } = this.props
			const pageIndex: number = items && items.findIndex(item => item.id === collectionItem.id)

			this.setActiveMarkerIcon(collectionItem)

			if (onSetActiveItem) onSetActiveItem(collectionItem, pageIndex)
		}
		this.setState((prevState: State) => ({
			markerListeners: [...prevState.markerListeners, listener],
		}))
		return listener
	}

	setActiveMarkerIcon = (collectionItem: CollectionItem | ScreenSection) => {
		const { markers } = this.state
		const { items, currentItemIndex } = this.props

		markers.forEach(item => {
			if (collectionItem && item.collectionItemId == collectionItem.id) {
				const { map_pin_colour: mapPinColour, map_pin_style: mapPinStyle }: any = collectionItem

				const markerElement = item.marker.getElement()
				markerElement.classList.add('active')
				const currentTourTitle = items?.[currentItemIndex]?.title || ''
				markerElement.setAttribute('aria-label', currentTourTitle)
				markerElement.style.backgroundImage = this.generateMapPinIconUrl(mapPinStyle, mapPinColour, 'active')
			} else {
				const itemElement = item.marker.getElement()
				if (itemElement.classList.contains('active')) {
					const { pinColor: mapPinColour, pinIcon: mapPinIcon, pinStyle: mapPinStyle } = item

					itemElement.classList.remove('active')
					itemElement.style.backgroundImage = this.generateMapPinIconUrl(mapPinStyle, mapPinColour, mapPinIcon)
				}
			}
		})
	}

	generateMapPinIconUrl = (style: string, color: string, icon: string): string =>
		`url(${generateMapPinIconUrl({ style, color, icon })})`

	isFeatureCollection = (geojson): boolean => geojson?.type === 'FeatureCollection'

	fitBounds = (showAllBounds?: boolean) => {
		const { markers, map } = this.state
		const { currentItemIndex, items, defaultZoom, mapRoutes } = this.props
		const hasCoordinates =
			items && items[currentItemIndex] && items[currentItemIndex].lat && items[currentItemIndex].lng

		if (hasCoordinates && !showAllBounds) {
			const { lng, lat } = items[currentItemIndex]
			this.state.map.flyTo({
				center: [Number(lng || 0), Number(lat || 0)],
				zoom: defaultZoom || (this.mapZoommed ? this.state.map.getZoom() : 15),
				duration: 0,
			})
			this.mapZoommed = true
		}

		if ((!hasCoordinates || showAllBounds) && (markers?.length || mapRoutes?.length)) {
			const bounds = new mapboxgl.LngLatBounds()

			markers.map(marker => {
				bounds.extend(new mapboxgl.LngLat(marker.lng as number, marker.lat as number))
			})
			let filtered = mapRoutes?.map(route => {
				const isFeatureCollection = this.isFeatureCollection(route)
				if (isFeatureCollection) {
					route.features = route.features.filter(
						feat => 'coordinates' in feat.geometry && feat.geometry.coordinates.length > 0,
					)
				}
				return route
			})
			filtered = filtered.filter(route => {
				const isFeatureCollection = this.isFeatureCollection(route)
				if (
					isFeatureCollection ||
					(Array.isArray(route.features) && route.features[0]?.geometry && 'coordinates' in route.features[0]?.geometry)
				) {
					return route
				}
			})
			filtered.map(route => {
				route.features.map(feat => {
					const coords = feat.geometry.coordinates
					if (Array.isArray(coords) && coords.length === 2 && typeof coords[0] == 'number' && typeof coords[1] == 'number') {
						bounds.extend(new mapboxgl.LngLat(coords[0], coords[1]))
					}
					else {
						feat.geometry.coordinates.map(coords => {
							bounds.extend(coords)
						})
					}
				})
			})
			if (!bounds.isEmpty()) {
				map.fitBounds(bounds, {
					padding: { top: 100, left: 60, right: 60, bottom: 150 },
					duration: 0,
				})
			}
		}
	}

	showAllBounds = (): void => this.fitBounds(true)

	getMyPosition = (): void => this.geolocate.trigger()

	_switchItemAction = (itemId: number, direction: SwitchDirection): void => {
		const { items, onSetActiveItem } = this.props
		const { markers } = this.state

		const { newItem, newItemIndex, pageIndex } = switchItemAction(itemId, direction, items)
		const { lat, lng } = markers[newItemIndex]

		this.state.map.flyTo({
			center: [Number(lng), Number(lat)],
		})

		this.setActiveMarkerIcon(newItem)

		if (onSetActiveItem) onSetActiveItem(newItem, pageIndex)
	}

	prevItem = (itemId: number) => this._switchItemAction(itemId, SwitchDirection.Prev)

	nextItem = (itemId: number) => this._switchItemAction(itemId, SwitchDirection.Next)

	onMountMapContainer = () => {
		this.setUpMap()
		this.setState({ mapContainerReady: true })
	}

	getRoute = async (routeProfile: RouteProfile, coordinates: SimpleCoordinates) => {
		setTimeout(() => this.state.map.resize())

		const { endCoordinates } = this.props

		if (!endCoordinates) {
			this.setState({ loading: false })
			return
		}

		const startCoords = `${coordinates.lng},${coordinates.lat}`
		const endCoords = `${endCoordinates.lng},${endCoordinates.lat}`
		try {
			const route = await getDirectionRoute(routeProfile, startCoords, endCoords)

			if (this.props.setLegsData && route.legs?.[0]) {
				this.props.setLegsData(route.legs[0])
			}

			const geojson: any = {
				type: 'Feature',
				properties: route.properties,
				geometry: route.geometry,
			}

			const layerOptions: any = {
				id: `directionRoute`,
				type: 'line',
				source: `directionRoute`,
				layout: {
					'line-join': 'round',
					'line-cap': 'round',
				},
				paint: {
					'line-color': ['coalesce', ['get', 'line-color'], '#3887be'],
					'line-width': 5,
					'line-opacity': 0.75,
				},
			}

			if (routeProfile === 'walking') {
				layerOptions.paint['line-dasharray'] = [1, 3]
			} else if (routeProfile === 'cycling') {
				layerOptions.paint['line-dasharray'] = [1, 1]
			}

			if (this.state.map.getLayer('directionRoute')) {
				this.state.map.removeLayer('directionRoute')
				this.state.map.removeSource('directionRoute')
			}

			this.state.map.addSource(`directionRoute`, {
				type: 'geojson',
				data: {
					type: 'FeatureCollection',
					features: [geojson],
				},
			})

			this.state.map.addLayer(layerOptions, 'waterway-label')

			const bounds = getBounds(
				route.geometry.coordinates.map((coords: number[]) => ({
					lng: coords[0],
					lat: coords[1],
				})),
			)

			this.setState({ loading: false })
			setTimeout(() => {
				this.state.map.fitBounds(bounds, {
					padding: { top: 100, left: 60, right: 60, bottom: 150 },
					duration: 0,
				})
			}, 1000)
		} catch (error) {
			console.error(error)
			this.setState({ error: JSON.stringify(error) })

			setTimeout(() => {
				this.setState({ loading: false })
				if (this.props.onCloseMap) {
					this.props.onCloseMap()
				}
			}, 3000)
		}
	}

	render() {
		const { mapControls, showDirections, items, currentItemIndex, isItemsLoaded } = this.props
		const { loading, error, mapContainerReady } = this.state

		return (
			<div
				style={{
					width: '100%',
					height: '100%',
				}}
				ref={(ref: HTMLDivElement) => {
					this.mapContainer = ref
					if (this.mapContainer && !this.state.mapContainerReady && (isItemsLoaded || showDirections)) {
						this.onMountMapContainer()
					}
				}}
			>
				{mapContainerReady &&
					mapControls &&
					mapControls({
						showAllBounds: this.showAllBounds,
						getMyPosition: this.getMyPosition,
						prevItem: this.prevItem,
						nextItem: this.nextItem,
						showInAppDirections: this.props.showInAppDirections,
					})}
				{this.state.currentRoute ? (
					<ElevationGraph
						route={this.state.currentRoute}
						defaultIndex={this.state.selectedRoutePointIndex}
						isOpen={this.state.elevationOpened}
						onMouseMove={this.setActiveElevationMarkerCoords}
						onMouseLeave={this.hideActiveElevationMarker}
						onToggle={this.toggleElevationGraph}
					/>
				) : null}
				{showDirections && <IonLoading isOpen={loading} message={strings.loading} />}
				<IonToast isOpen={!!error} message={error && error.substring(0, 160)} duration={3000} />
				<span className="hidden" aria-live="assertive">
					{items?.[currentItemIndex]?.title}
				</span>
			</div>
		)
	}
}
